import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import { FaCheck } from 'react-icons/fa';
import {
	applyBidListAuditSelections,
	BidListAuditResult,
	BidListAuditSuggestion,
	getBidListAuditResult,
	getLatestBidListAudit,
	markBidListAuditPresented,
	startBidListAudit,
} from './publishApi';

interface BidListAuditModalProps {
	initialRun?: BidListAuditResult | null;
	onCancel: () => void;
	onRunChange?: (run: BidListAuditResult) => void;
	onSend: () => Promise<void>;
	onSkip: () => void;
	projectId: number;
}

type AuditStep = 'CHOICE' | 'RUNNING' | 'RESULTS' | 'FAILED' | 'POLLING_ERROR';

const selectionKey = (suggestion: BidListAuditSuggestion) =>
	[suggestion.bidPackageId, suggestion.suggestionId || suggestion.vendorCompanyId].join(':');

const runIdentity = (run?: BidListAuditResult | null) =>
	String(run?.runId || run?.responseId || '');

const BidListAuditModal: FC<BidListAuditModalProps> = ({
	initialRun = null,
	onCancel,
	onRunChange,
	onSend,
	onSkip,
	projectId,
}) => {
	const [step, setStep] = useState<AuditStep>('CHOICE');
	const [result, setResult] = useState<BidListAuditResult | null>(null);
	const [selected, setSelected] = useState<string[]>([]);
	const [error, setError] = useState('');
	const [sending, setSending] = useState(false);
	const [auditInstructions, setAuditInstructions] = useState('');
	const mounted = useRef(true);
	const presentedRunId = useRef<number | null>(null);
	const selectedRunIdentity = useRef('');
	const pollingFailureCount = useRef(0);
	const resultStatus = result?.status;
	const resultRunId = result?.runId;
	const resultResponseId = result?.responseId;
	const resultPresentedAt = result?.presentedAt;

	useEffect(
		() => () => {
			mounted.current = false;
		},
		[],
	);

	const applyRun = useCallback(
		(nextRun: BidListAuditResult) => {
			if (!mounted.current) return;
			setResult(nextRun);
			if (onRunChange) onRunChange(nextRun);
			if (nextRun.status === 'PROCESSING') {
				pollingFailureCount.current = 0;
				setError('');
				setStep('RUNNING');
				return;
			}
			if (nextRun.status === 'FAILED') {
				pollingFailureCount.current = 0;
				setError(nextRun.errorMessage || 'The bid list audit could not be completed.');
				setStep('FAILED');
				return;
			}
			const identity = runIdentity(nextRun);
			if (identity && selectedRunIdentity.current !== identity) {
				selectedRunIdentity.current = identity;
				setSelected([]);
			}
			pollingFailureCount.current = 0;
			setError('');
			setStep('RESULTS');
		},
		[onRunChange],
	);

	useEffect(() => {
		let active = true;
		const hydrate = async () => {
			try {
				const latest = initialRun || (await getLatestBidListAudit(projectId));
				if (active && latest && (latest.status === 'PROCESSING' || !latest.presentedAt)) {
					applyRun(latest);
				}
			} catch (_error) {
				// Starting a new review remains available when latest-run hydration is unavailable.
			}
		};
		hydrate();
		return () => {
			active = false;
		};
	}, [applyRun, initialRun, projectId]);

	useEffect(() => {
		if (resultStatus !== 'PROCESSING' || step !== 'RUNNING') return undefined;
		const identifier = resultRunId || resultResponseId;
		if (!identifier) {
			setError('The bid list audit did not start correctly.');
			setStep('POLLING_ERROR');
			return undefined;
		}
		let active = true;
		let polling = false;
		const poll = async () => {
			if (polling) return;
			polling = true;
			try {
				const nextRun = await getBidListAuditResult(projectId, identifier);
				if (active) applyRun(nextRun);
			} catch (requestError) {
				if (active) {
					pollingFailureCount.current += 1;
					setError(
						requestError?.data?.message ||
							requestError?.message ||
							'Unable to refresh the bid list audit.',
					);
					if (pollingFailureCount.current >= 5) setStep('POLLING_ERROR');
				}
			} finally {
				polling = false;
			}
		};
		const timer = window.setInterval(poll, 4000);
		return () => {
			active = false;
			window.clearInterval(timer);
		};
	}, [applyRun, projectId, resultResponseId, resultRunId, resultStatus, step]);

	useEffect(() => {
		const runId = Number(resultRunId || 0);
		if (step !== 'RESULTS' || runId < 1 || resultPresentedAt || presentedRunId.current === runId) {
			return;
		}
		presentedRunId.current = runId;
		markBidListAuditPresented(projectId, runId)
			.then(applyRun)
			.catch(() => {
				presentedRunId.current = null;
			});
	}, [applyRun, projectId, resultPresentedAt, resultRunId, step]);

	const runAudit = async () => {
		setStep('RUNNING');
		setError('');
		try {
			applyRun(await startBidListAudit(projectId, auditInstructions));
		} catch (requestError) {
			if (!mounted.current) return;
			setError(
				requestError?.data?.message ||
					requestError?.message ||
					'Unable to complete the bid list audit.',
			);
			setStep('CHOICE');
		}
	};

	const retryStatus = async () => {
		const identifier = result?.runId || result?.responseId;
		if (!identifier) {
			setError('The bid list audit did not start correctly.');
			setStep('POLLING_ERROR');
			return;
		}
		setError('');
		try {
			applyRun(await getBidListAuditResult(projectId, identifier));
		} catch (requestError) {
			if (!mounted.current) return;
			setError(
				requestError?.data?.message ||
					requestError?.message ||
					'Unable to refresh the bid list audit.',
			);
			setStep('POLLING_ERROR');
		}
	};

	const toggleSuggestion = (suggestion: BidListAuditSuggestion) => {
		const key = selectionKey(suggestion);
		setSelected((current) =>
			current.includes(key) ? current.filter((item) => item !== key) : [...current, key],
		);
	};

	const sendInvitations = async () => {
		setSending(true);
		setError('');
		try {
			if (selected.length) {
				if (!result?.responseId) {
					throw new Error('The bid list audit result is unavailable. Please run it again.');
				}
				const selections = result.suggestions
					.filter((suggestion) => selected.includes(selectionKey(suggestion)))
					.map((suggestion) => {
						if (suggestion.suggestionId) {
							return {
								bidPackageId: suggestion.bidPackageId,
								suggestionId: suggestion.suggestionId,
							};
						}
						return {
							bidPackageId: suggestion.bidPackageId,
							vendorCompanyId: suggestion.vendorCompanyId,
						};
					});
				await applyBidListAuditSelections(projectId, result.responseId, selections);
			}
			await onSend();
		} catch (requestError) {
			if (!mounted.current) return;
			setError(
				requestError?.data?.message ||
					requestError?.message ||
					'Unable to prepare the bid invitations.',
			);
		} finally {
			if (mounted.current) setSending(false);
		}
	};

	const groups = (result?.suggestions || []).reduce(
		(grouped: { [bidPackageId: string]: BidListAuditSuggestion[] }, suggestion) => {
			const key = String(suggestion.bidPackageId);
			grouped[key] = [...(grouped[key] || []), suggestion];
			return grouped;
		},
		{},
	);

	let title = 'Bid list audit recommendations';
	let description =
		"Use the checkboxes to add bidders to this project. Each recommendation is based on your organization's recent bidding history.";
	if (step === 'CHOICE') {
		title = 'Would you like a second look at your bid list?';
		description =
			'ScrubPlan can compare this project with similar work from this office to recommend other bidders with the highest probability of submitting a bid.';
	} else if (step === 'RUNNING') {
		title = 'Reviewing your bidder network';
		description = 'We are comparing recent projects, bidder participation, and office patterns.';
	} else if (step === 'FAILED') {
		title = 'The bid list review needs another attempt';
		description =
			'The saved review did not complete. You can start a new review or continue without one.';
	} else if (step === 'POLLING_ERROR') {
		title = 'We lost contact with the bid list review';
		description =
			'The review may still be running. Retry its status or continue without the review.';
	}

	let sendButtonLabel = "Don't add bidders, send invites";
	if (sending) {
		sendButtonLabel = 'Sending invitations...';
	} else if (selected.length) {
		sendButtonLabel = 'Add bidders and send invites';
	}

	return (
		<div className="bid-invites-modal-overlay" role="dialog" aria-modal="true" aria-label={title}>
			<div className="bid-invites-dialog bid-list-audit-dialog">
				<header className="bid-invites-dialog-header bid-list-audit-header">
					<div>
						<span>SMART BIDDER REVIEW</span>
						<h2>{title}</h2>
						<p>{description}</p>
					</div>
				</header>
				<div className="bid-invites-dialog-body">
					{error && (
						<div className="bid-list-audit-error" role="alert">
							{error}
						</div>
					)}

					{step === 'CHOICE' && (
						<div className="bid-list-audit-choice">
							<div className="bid-list-audit-choice-icon" aria-hidden="true">
								<FaCheck />
							</div>
							<div>
								<h3>Bid list audit</h3>
								<p>
									Recommendations consider recent bid submissions, market-sector experience, and the
									bidders your office consistently invites. You stay in control and choose whether
									to add anyone.
								</p>
								<ul>
									<li>Existing bidder history and qualified web-found companies are considered.</li>
									<li>No invitations are sent until you confirm the final list.</li>
								</ul>
								<label
									className="bid-list-audit-instructions"
									htmlFor="bid-list-audit-instructions"
								>
									<span>Specific audit requirements (optional)</span>
									<textarea
										aria-label="Specific audit requirements (optional)"
										id="bid-list-audit-instructions"
										maxLength={4000}
										onChange={(event) => setAuditInstructions(event.target.value)}
										placeholder="For example: prioritize bidders with healthcare experience or recent bids in this region."
										rows={4}
										value={auditInstructions}
									/>
									<small>
										Add project-specific requirements or priorities to help ScrubPlan refine the
										audit results.
									</small>
								</label>
							</div>
						</div>
					)}

					{step === 'RUNNING' && (
						<div className="bid-list-audit-loading">
							<div className="bid-list-audit-spinner" aria-hidden="true" />
							<strong>Building tailored recommendations...</strong>
							<span>This can take a few minutes. You can close this window.</span>
						</div>
					)}

					{(step === 'FAILED' || step === 'POLLING_ERROR') && (
						<div className="bid-list-audit-empty">
							<strong>
								{step === 'FAILED'
									? 'No invitations have been sent.'
									: 'Automatic status checks have stopped.'}
							</strong>
							<span>You remain in control of whether to review again or continue.</span>
						</div>
					)}

					{step === 'RESULTS' && (
						<div className="bid-list-audit-results">
							{Boolean(result?.requirementCoverage?.projected?.length) && (
								<section className="bid-list-audit-requirements">
									<h3>Project bid requirement coverage</h3>
									{result!.requirementCoverage!.projected.map((coverage) => {
										const current = result!.requirementCoverage!.current.find(
											(item) => item.requirementId === coverage.requirementId,
										);
										return (
											<div key={coverage.requirementId}>
												<strong>{coverage.values.join(' + ')}</strong>
												<span>
													{current?.achievedPercentage || 0}% current →{' '}
													{coverage.achievedPercentage}% projected (minimum{' '}
													{coverage.minimumPercentage}%)
												</span>
												{coverage.shortfallCount > 0 && (
													<small>
														{coverage.shortfallCount} additional qualifying{' '}
														{coverage.shortfallCount === 1 ? 'company is' : 'companies are'} needed.
													</small>
												)}
											</div>
										);
									})}
								</section>
							)}
							{Object.keys(groups).length === 0 && (
								<div className="bid-list-audit-empty">
									<strong>Your current bid list looks well covered.</strong>
									<span>No additional bidders met the recommendation criteria.</span>
								</div>
							)}
							{Object.keys(groups).map((bidPackageId) => (
								<section key={bidPackageId}>
									<h3>{groups[bidPackageId][0].bidPackageName}</h3>
									{groups[bidPackageId].map((suggestion) => {
										const key = selectionKey(suggestion);
										return (
											<label
												className={`bid-list-audit-suggestion${
													selected.includes(key) ? ' selected' : ''
												}`}
												key={key}
											>
												<input
													aria-label={`Add ${suggestion.vendorCompanyName}`}
													checked={selected.includes(key)}
													onChange={() => toggleSuggestion(suggestion)}
													type="checkbox"
												/>
												<span>
													<strong>{suggestion.vendorCompanyName}</strong>
													<small>{suggestion.reason}</small>
													{(suggestion.suggestedContacts || []).map((contact, index) => (
														<small
															key={
																String(index) +
																String(contact.email || contact.phone || contact.fullName)
															}
														>
															{[contact.fullName, contact.email, contact.phone]
																.filter(Boolean)
																.join(' - ')}
														</small>
													))}
													{(suggestion.sources || []).length > 0 && (
														<span className="bid-list-agent-sources">
															{(suggestion.sources || []).map((source) => (
																<a
																	href={source.url}
																	key={source.url}
																	rel="noopener noreferrer"
																	target="_blank"
																>
																	{source.title}
																</a>
															))}
														</span>
													)}
												</span>
											</label>
										);
									})}
								</section>
							))}
						</div>
					)}
				</div>
				<footer className="bid-invites-dialog-footer">
					{step === 'CHOICE' && (
						<>
							<button type="button" className="bid-invites-button" onClick={onSkip}>
								No, continue without an audit
							</button>
							<button type="button" className="bid-invites-button primary" onClick={runAudit}>
								Yes, review my bid list
							</button>
						</>
					)}
					{step === 'RUNNING' && (
						<button type="button" className="bid-invites-button" onClick={onCancel}>
							Close
						</button>
					)}
					{step === 'POLLING_ERROR' && (
						<>
							<button type="button" className="bid-invites-button" onClick={onCancel}>
								Close
							</button>
							<button type="button" className="bid-invites-button" onClick={onSkip}>
								Continue without review
							</button>
							<button type="button" className="bid-invites-button primary" onClick={retryStatus}>
								Retry status
							</button>
						</>
					)}
					{step === 'FAILED' && (
						<>
							<button type="button" className="bid-invites-button" onClick={onCancel}>
								Close
							</button>
							<button type="button" className="bid-invites-button" onClick={onSkip}>
								Continue without review
							</button>
							<button type="button" className="bid-invites-button primary" onClick={runAudit}>
								Try review again
							</button>
						</>
					)}
					{step === 'RESULTS' && (
						<>
							<button type="button" className="bid-invites-button" onClick={onCancel}>
								Cancel
							</button>
							<button
								type="button"
								className="bid-invites-button primary"
								disabled={sending}
								onClick={sendInvitations}
							>
								{sendButtonLabel}
							</button>
						</>
					)}
				</footer>
			</div>
		</div>
	);
};

export default BidListAuditModal;
